import { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Trophy, Zap, Grid3x3, Calculator, Shuffle, Keyboard, Layers, Puzzle } from "lucide-react";
import { toast } from "sonner";

const GAMES = [
  { id: "memory", name: "Memory Match", icon: Grid3x3, blurb: "Flip cards & match pairs. Beat the clock." },
  { id: "math", name: "Math Challenge", icon: Calculator, blurb: "Solve 20 arithmetic problems as fast as you can." },
  { id: "sequence", name: "Pattern Recall", icon: Shuffle, blurb: "Watch the sequence, repeat it. Simon-style." },
  { id: "typing", name: "Typing Speed Test", icon: Keyboard, blurb: "How fast can you type? WPM + accuracy score." },
  { id: "sudoku", name: "Sudoku", icon: Layers, blurb: "Classic 4×4 mini-sudoku. Fill without repeats." },
  { id: "puzzle", name: "Slider Puzzle", icon: Puzzle, blurb: "3×3 slider puzzle. Arrange numbers in order." },
];

export default function StudentGames() {
  const [active, setActive] = useState(null);
  const [leaderboard, setLeaderboard] = useState({});

  useEffect(() => {
    GAMES.forEach(async (g) => {
      try {
        const res = await api.get(`/games/leaderboard?game=${g.id}`);
        setLeaderboard((lb) => ({ ...lb, [g.id]: res.data }));
      } catch { /* ignore */ }
    });
  }, [active]);

  const submitScore = async (game, score, duration_ms) => {
    try { await api.post("/games/score", { game, score, duration_ms }); toast.success(`Score saved: ${score}`); }
    catch { /* ignore */ }
  };

  return (
    <Layout title="Brain Games" subtitle="Sharpen your mind">
      {!active && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {GAMES.map((g) => {
            const Icon = g.icon;
            const board = leaderboard[g.id] || [];
            return (
              <div key={g.id} data-testid={`game-card-${g.id}`} className="bg-white border border-slate-200 rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 text-white flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="mt-4 font-display font-bold text-xl text-slate-900">{g.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{g.blurb}</p>
                <button data-testid={`play-${g.id}`} onClick={() => setActive(g.id)} className="mt-4 px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700 inline-flex items-center gap-1.5">
                  <Zap className="w-4 h-4" /> Play now
                </button>
                {board.length > 0 && (
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 flex items-center gap-1"><Trophy className="w-3 h-3" /> Top scores</div>
                    <div className="space-y-1">
                      {board.slice(0, 3).map((row, i) => (
                        <div key={row.user_id} className="text-xs text-slate-600 flex justify-between">
                          <span className="truncate">{i + 1}. {row.email?.split("@")[0]}</span>
                          <span className="font-mono">{row.best}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {active === "memory" && <MemoryGame onExit={() => setActive(null)} onScore={(s, d) => submitScore("memory", s, d)} />}
      {active === "math" && <MathSprint onExit={() => setActive(null)} onScore={(s, d) => submitScore("math", s, d)} />}
      {active === "sequence" && <SequenceGame onExit={() => setActive(null)} onScore={(s, d) => submitScore("sequence", s, d)} />}
      {active === "typing" && <TypingGame onExit={() => setActive(null)} onScore={(s, d) => submitScore("typing", s, d)} />}
      {active === "sudoku" && <SudokuGame onExit={() => setActive(null)} onScore={(s, d) => submitScore("sudoku", s, d)} />}
      {active === "puzzle" && <SliderPuzzle onExit={() => setActive(null)} onScore={(s, d) => submitScore("puzzle", s, d)} />}
    </Layout>
  );
}

/* ---------------- Memory Match ---------------- */
function MemoryGame({ onExit, onScore }) {
  const emojis = ["◆", "★", "●", "▲", "♥", "♪", "☀", "⚡"];
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const startRef = useRef(Date.now());
  const [done, setDone] = useState(false);

  useEffect(() => {
    const deck = [...emojis, ...emojis].map((v, i) => ({ v, i })).sort(() => Math.random() - 0.5);
    setCards(deck);
    startRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (flipped.length === 2) {
      const [a, b] = flipped;
      if (cards[a].v === cards[b].v) {
        setMatched((m) => [...m, a, b]);
      }
      setTimeout(() => setFlipped([]), 700);
      setMoves((m) => m + 1);
    }
  }, [flipped, cards]);

  useEffect(() => {
    if (matched.length === cards.length && cards.length > 0 && !done) {
      const dur = Date.now() - startRef.current;
      const score = Math.max(0, 1000 - moves * 30 - Math.floor(dur / 1000));
      setDone(true);
      onScore(score, dur);
    }
  }, [matched, cards, moves, done, onScore]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="memory-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Memory Match</div>
          <h2 className="font-display font-bold text-xl">Moves: {moves} · Matched: {matched.length / 2}/{cards.length / 2}</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-4 gap-3 max-w-md">
        {cards.map((c, idx) => {
          const isRevealed = flipped.includes(idx) || matched.includes(idx);
          return (
            <button
              key={idx}
              data-testid={`mem-card-${idx}`}
              onClick={() => !isRevealed && flipped.length < 2 && setFlipped((f) => [...f, idx])}
              className={`aspect-square rounded-xl text-2xl font-bold flex items-center justify-center transition-colors ${
                isRevealed ? "bg-blue-100 text-blue-700" : "bg-slate-800 text-slate-800 hover:bg-slate-700"
              }`}
            >
              {isRevealed ? c.v : "?"}
            </button>
          );
        })}
      </div>
      {done && <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800 text-sm" data-testid="game-done">Complete! Score saved.</div>}
    </div>
  );
}

/* ---------------- Math Sprint ---------------- */
function MathSprint({ onExit, onScore }) {
  const gen = () => {
    const a = Math.floor(Math.random() * 20) + 1;
    const b = Math.floor(Math.random() * 20) + 1;
    const ops = ["+", "-", "×"];
    const op = ops[Math.floor(Math.random() * 3)];
    const ans = op === "+" ? a + b : op === "-" ? a - b : a * b;
    return { a, b, op, ans };
  };
  const [q, setQ] = useState(gen());
  const [ans, setAns] = useState("");
  const [score, setScore] = useState(0);
  const [i, setI] = useState(0);
  const startRef = useRef(Date.now());
  const [done, setDone] = useState(false);
  const TOTAL = 20;

  const submit = () => {
    if (ans === "") return;
    if (parseInt(ans, 10) === q.ans) setScore((s) => s + 50);
    setAns("");
    if (i + 1 >= TOTAL) {
      const dur = Date.now() - startRef.current;
      setDone(true); onScore(score + (parseInt(ans, 10) === q.ans ? 50 : 0), dur);
    } else {
      setI((v) => v + 1); setQ(gen());
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md" data-testid="math-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Math Sprint</div>
          <h2 className="font-display font-bold text-xl">Q {Math.min(i + 1, TOTAL)}/{TOTAL} · Score {score}</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      {!done ? (
        <>
          <div className="text-4xl font-display font-black text-slate-900 mb-4">{q.a} {q.op} {q.b} = ?</div>
          <input
            data-testid="math-answer"
            autoFocus
            value={ans}
            type="number"
            onChange={(e) => setAns(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none text-2xl font-mono"
          />
          <button data-testid="math-submit" onClick={submit} className="mt-3 w-full py-2 rounded-full bg-blue-600 text-white">Submit</button>
        </>
      ) : (
        <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800" data-testid="game-done">
          Finished! Final score: <b>{score}</b>
        </div>
      )}
    </div>
  );
}

/* ---------------- Sequence Recall ---------------- */
function SequenceGame({ onExit, onScore }) {
  const [seq, setSeq] = useState([]);
  const [user, setUser] = useState([]);
  const [showing, setShowing] = useState(false);
  const [round, setRound] = useState(0);
  const [current, setCurrent] = useState(-1);
  const startRef = useRef(Date.now());
  const [failed, setFailed] = useState(false);

  const colors = ["bg-red-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500"];

  const nextRound = () => {
    const nxt = [...seq, Math.floor(Math.random() * 4)];
    setSeq(nxt); setUser([]); setRound(nxt.length);
    setShowing(true);
    nxt.forEach((v, i) => {
      setTimeout(() => setCurrent(v), 600 * i + 400);
      setTimeout(() => setCurrent(-1), 600 * i + 800);
    });
    setTimeout(() => setShowing(false), 600 * nxt.length + 400);
  };

  useEffect(() => { if (seq.length === 0) nextRound(); }, []);

  const press = (i) => {
    if (showing || failed) return;
    const nUser = [...user, i];
    setUser(nUser);
    if (seq[nUser.length - 1] !== i) {
      setFailed(true);
      const dur = Date.now() - startRef.current;
      onScore(round - 1, dur);
      return;
    }
    if (nUser.length === seq.length) setTimeout(nextRound, 500);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md" data-testid="sequence-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Pattern Recall</div>
          <h2 className="font-display font-bold text-xl">Round {round}</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <button
            key={i}
            data-testid={`seq-btn-${i}`}
            onClick={() => press(i)}
            className={`aspect-square rounded-2xl transition-opacity duration-200 ${colors[i]} ${current === i ? "opacity-100 ring-4 ring-white" : "opacity-60 hover:opacity-100"}`}
          />
        ))}
      </div>
      {failed && (
        <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-red-800 text-sm" data-testid="game-done">
          Missed! You reached round {round}. Score saved.
        </div>
      )}
    </div>
  );
}

/* ---------------- Typing Speed ---------------- */
function TypingGame({ onExit, onScore }) {
  const SAMPLE = "the quick brown fox jumps over the lazy dog and enjoys learning at trillion bytex";
  const [input, setInput] = useState("");
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState(false);
  const startRef = useRef(0);
  const [result, setResult] = useState(null);

  const change = (v) => {
    if (!started && v.length > 0) { setStarted(true); startRef.current = Date.now(); }
    setInput(v);
    if (v.length >= SAMPLE.length) finish(v);
  };

  const finish = (v) => {
    const dur = Date.now() - startRef.current;
    const words = SAMPLE.split(" ").length;
    const minutes = dur / 60000;
    const wpm = Math.round(words / Math.max(minutes, 0.001));
    let correct = 0;
    for (let i = 0; i < SAMPLE.length; i++) if (v[i] === SAMPLE[i]) correct++;
    const acc = Math.round((correct / SAMPLE.length) * 100);
    const score = Math.max(0, wpm * (acc / 100) * 10);
    setResult({ wpm, acc, score: Math.round(score) });
    setDone(true);
    onScore(Math.round(score), dur);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl" data-testid="typing-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Typing Speed Test</div>
          <h2 className="font-display font-bold text-xl">Type the sentence as fast as you can</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <div className="font-mono text-slate-500 text-sm bg-slate-50 rounded-xl p-3 mb-3 leading-relaxed">
        {SAMPLE.split("").map((ch, i) => {
          const cls = i < input.length ? (input[i] === ch ? "text-emerald-600" : "text-red-500 bg-red-100") : "";
          return <span key={i} className={cls}>{ch}</span>;
        })}
      </div>
      <textarea
        data-testid="typing-input" value={input} onChange={(e) => change(e.target.value)}
        disabled={done} autoFocus
        className="w-full min-h-[80px] px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-mono"
        placeholder="Start typing here…"
      />
      {done && (
        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="game-done">
          <div className="bg-blue-50 rounded-xl p-3 text-center"><div className="text-2xl font-display font-black text-blue-700">{result.wpm}</div><div className="text-xs text-blue-600">WPM</div></div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center"><div className="text-2xl font-display font-black text-emerald-700">{result.acc}%</div><div className="text-xs text-emerald-600">accuracy</div></div>
          <div className="bg-violet-50 rounded-xl p-3 text-center"><div className="text-2xl font-display font-black text-violet-700">{result.score}</div><div className="text-xs text-violet-600">score</div></div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Mini Sudoku (4×4) ---------------- */
function SudokuGame({ onExit, onScore }) {
  // 4x4 sudoku puzzle (0 = blank)
  const puzzle = [
    [1, 0, 0, 4],
    [0, 3, 2, 0],
    [0, 2, 4, 0],
    [4, 0, 0, 2],
  ];
  const solution = [
    [1, 2, 3, 4],
    [3, 4, 1, 2],
    [2, 1, 4, 3],  // note: this row is illustrative — we'll just verify by no-repeat rule
    [4, 3, 2, 1],
  ];
  const [grid, setGrid] = useState(puzzle.map((r) => [...r]));
  const startRef = useRef(Date.now());
  const [done, setDone] = useState(false);

  const set = (r, c, val) => {
    const v = parseInt(val || "0", 10);
    if (v < 0 || v > 4) return;
    if (puzzle[r][c] !== 0) return;
    const g = grid.map((row) => [...row]);
    g[r][c] = v;
    setGrid(g);
  };

  const isValid = () => {
    // No repeat in row / col; complete
    for (let i = 0; i < 4; i++) {
      const row = new Set(); const col = new Set();
      for (let j = 0; j < 4; j++) {
        if (!grid[i][j] || !grid[j][i]) return false;
        if (row.has(grid[i][j])) return false;
        if (col.has(grid[j][i])) return false;
        row.add(grid[i][j]); col.add(grid[j][i]);
      }
    }
    return true;
  };

  const check = () => {
    if (isValid()) {
      const dur = Date.now() - startRef.current;
      const score = Math.max(0, 500 - Math.floor(dur / 1000));
      setDone(true);
      onScore(score, dur);
      toast.success(`Solved! Score ${score}`);
    } else {
      toast.error("Not valid — no repeats in rows or columns");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md" data-testid="sudoku-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Mini Sudoku · 4×4</div>
          <h2 className="font-display font-bold text-xl">Fill 1–4, no repeats in rows/cols</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <div className="grid grid-cols-4 gap-1.5 max-w-xs">
        {grid.map((row, r) => row.map((v, c) => {
          const preset = puzzle[r][c] !== 0;
          return (
            <input
              key={`${r}-${c}`}
              data-testid={`sudoku-cell-${r}-${c}`}
              value={v || ""}
              onChange={(e) => set(r, c, e.target.value)}
              disabled={preset || done}
              maxLength={1}
              className={`aspect-square rounded-lg text-center text-lg font-display font-bold outline-none ${preset ? "bg-slate-100 text-slate-500" : "bg-white border-2 border-slate-200 focus:border-blue-500 text-slate-900"}`}
            />
          );
        }))}
      </div>
      <button data-testid="sudoku-check" onClick={check} disabled={done} className="mt-4 w-full py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50">Check solution</button>
      {done && <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800 text-sm" data-testid="game-done">Solved! Score saved.</div>}
    </div>
  );
}

/* ---------------- Slider Puzzle (3×3) ---------------- */
function SliderPuzzle({ onExit, onScore }) {
  const [tiles, setTiles] = useState([1, 2, 3, 4, 5, 6, 7, 8, 0]);
  const [moves, setMoves] = useState(0);
  const startRef = useRef(Date.now());
  const [done, setDone] = useState(false);

  useEffect(() => {
    // shuffle - do 100 random valid moves
    let arr = [1, 2, 3, 4, 5, 6, 7, 8, 0];
    for (let k = 0; k < 100; k++) {
      const zi = arr.indexOf(0);
      const nbrs = neighbors(zi);
      const swap = nbrs[Math.floor(Math.random() * nbrs.length)];
      [arr[zi], arr[swap]] = [arr[swap], arr[zi]];
    }
    setTiles(arr); setMoves(0); startRef.current = Date.now();
  }, []);

  const neighbors = (i) => {
    const r = Math.floor(i / 3), c = i % 3;
    const list = [];
    if (r > 0) list.push(i - 3);
    if (r < 2) list.push(i + 3);
    if (c > 0) list.push(i - 1);
    if (c < 2) list.push(i + 1);
    return list;
  };

  const click = (i) => {
    if (done) return;
    const zi = tiles.indexOf(0);
    if (!neighbors(zi).includes(i)) return;
    const arr = [...tiles];
    [arr[zi], arr[i]] = [arr[i], arr[zi]];
    setTiles(arr); setMoves((m) => m + 1);
    // check
    if (arr.slice(0, 8).every((v, idx) => v === idx + 1) && arr[8] === 0) {
      const dur = Date.now() - startRef.current;
      const score = Math.max(0, 800 - moves * 5 - Math.floor(dur / 1000));
      setDone(true);
      onScore(score, dur);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-md" data-testid="slider-puzzle-game">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Slider Puzzle · 3×3</div>
          <h2 className="font-display font-bold text-xl">Moves: {moves}</h2>
        </div>
        <button onClick={onExit} data-testid="exit-game" className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <div className="grid grid-cols-3 gap-1.5 max-w-xs">
        {tiles.map((v, i) => (
          <button
            key={i} data-testid={`tile-${i}`}
            onClick={() => click(i)}
            className={`aspect-square rounded-lg text-2xl font-display font-black transition-colors ${v === 0 ? "bg-slate-100 cursor-default" : "bg-blue-600 text-white hover:bg-blue-700"}`}
          >{v || ""}</button>
        ))}
      </div>
      {done && <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800 text-sm" data-testid="game-done">Solved in {moves} moves! Score saved.</div>}
    </div>
  );
}

